// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import * as React from 'react'
import classNames from 'classnames'
import CalendarOutlined from '@ant-design/icons/CalendarOutlined'
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined'
import CloseCircleFilled from '@ant-design/icons/CloseCircleFilled'
import SwapRightOutlined from '@ant-design/icons/SwapRightOutlined'
import { RangePicker as RCRangePicker } from '@kartacam/rc-picker'
import enUS from 'antd/es/date-picker/locale/en_US'
import { ConfigContext } from 'antd/es/config-provider'
import SizeContext from 'antd/es/config-provider/SizeContext'
import LocaleReceiver from 'antd/es/locale-provider/LocaleReceiver'
import { getRangePlaceholder } from '../util'
import { getTimeProps, Components } from '.'

export default function generateRangePicker (generateConfig) {
  class RangePicker extends React.Component {
    static contextType = ConfigContext

    context

    pickerRef = React.createRef()

    focus = () => {
      if (this.pickerRef.current) {
        this.pickerRef.current.focus()
      }
    }

    blur = () => {
      if (this.pickerRef.current) {
        this.pickerRef.current.blur()
      }
    }

    getDefaultLocale = () => {
      const { locale } = this.props
      const result = {
        ...enUS,
        ...locale
      }
      result.lang = {
        ...result.lang,
        ...(locale || {}).lang
      }
      return result
    }

    renderPicker = locale => {
      const { getPrefixCls, direction, getPopupContainer } = this.context
      const {
        prefixCls: customizePrefixCls,
        getPopupContainer: customGetPopupContainer,
        className,
        size: customizeSize,
        bordered = true,
        placeholder,
        ...restProps
      } = this.props
      const { format, showTime, picker } = this.props
      const prefixCls = getPrefixCls('picker', customizePrefixCls)

      let additionalOverrideProps = {}

      additionalOverrideProps = {
        ...additionalOverrideProps,
        ...(showTime ? getTimeProps({ format, picker, ...showTime }) : {}),
        ...(picker === 'time' ? getTimeProps({ format, ...this.props, picker }) : {})
      }

      return (
        <SizeContext.Consumer>
          {size => {
            const mergedSize = customizeSize || size

            return (
              <RCRangePicker
                separator={
                  <span aria-label='to' className={`${prefixCls}-separator`}>
                    <SwapRightOutlined />
                  </span>
                }
                ref={this.pickerRef}
                placeholder={getRangePlaceholder(picker, locale, placeholder)}
                suffixIcon={picker === 'time' ? <ClockCircleOutlined /> : <CalendarOutlined />}
                clearIcon={<CloseCircleFilled />}
                allowClear
                transitionName='slide-up'
                {...restProps}
                {...additionalOverrideProps}
                className={classNames(className, {
                  [`${prefixCls}-${mergedSize}`]: mergedSize,
                  [`${prefixCls}-borderless`]: !bordered
                })}
                locale={locale.lang}
                prefixCls={prefixCls}
                getPopupContainer={customGetPopupContainer || getPopupContainer}
                generateConfig={generateConfig}
                prevIcon={<span className={`${prefixCls}-prev-icon`} />}
                nextIcon={<span className={`${prefixCls}-next-icon`} />}
                superPrevIcon={<span className={`${prefixCls}-super-prev-icon`} />}
                superNextIcon={<span className={`${prefixCls}-super-next-icon`} />}
                components={Components}
                direction={direction}
              />
            )
          }}
        </SizeContext.Consumer>
      )
    }

    render () {
      return (
        <LocaleReceiver componentName='DatePicker' defaultLocale={this.getDefaultLocale}>
          {this.renderPicker}
        </LocaleReceiver>
      )
    }
  }

  return RangePicker
}
